import { useEffect, useState } from 'react';
import { GameStateProvider } from './hooks/useGameState';
import { Layout } from './components/Layout';
import { HomeScreen } from './screens/HomeScreen';
import { CollectionScreen } from './screens/CollectionScreen';
import { GachaScreen } from './screens/GachaScreen';
import { ShopScreen } from './screens/ShopScreen';
import { StoneDetailScreen } from './screens/StoneDetailScreen';
import { SakuraBackground } from './components/SakuraBackground';

export type Screen = 'home' | 'collection' | 'gacha' | 'shop' | 'detail';

/** ハッシュベースでルーティング。
 *  #/         → home
 *  #/collection → collection
 *  #/gacha    → gacha
 *  #/shop     → shop
 *  #/stone/:id → detail
 */
function parseHash(): { screen: Screen; stoneId?: string } {
  const raw = window.location.hash.replace(/^#\/?/, '');
  if (raw.startsWith('stone/')) {
    return { screen: 'detail', stoneId: raw.slice('stone/'.length) };
  }
  if (raw === 'collection' || raw === 'gacha' || raw === 'shop') {
    return { screen: raw };
  }
  return { screen: 'home' };
}

function toHash(next: Screen, stoneId?: string): string {
  if (next === 'detail' && stoneId) return `#/stone/${stoneId}`;
  if (next === 'home') return '#/';
  return `#/${next}`;
}

export default function App() {
  return (
    <GameStateProvider>
      <Shell />
    </GameStateProvider>
  );
}

function Shell() {
  const [{ screen, stoneId }, setRoute] = useState(() => parseHash());

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = (next: Screen, id?: string) => {
    window.location.hash = toHash(next, id);
  };

  const navForLayout =
    screen === 'detail' ? 'home' : (screen as 'home' | 'collection' | 'gacha' | 'shop');

  return (
    <>
      <SakuraBackground count={16} />
      <Layout current={navForLayout} onNavigate={(n) => navigate(n)}>
        {screen === 'home' && <HomeScreen onNavigate={navigate} />}
        {screen === 'collection' && <CollectionScreen onNavigate={navigate} />}
        {screen === 'gacha' && <GachaScreen onNavigate={navigate} />}
        {screen === 'shop' && <ShopScreen onNavigate={navigate} />}
        {screen === 'detail' && stoneId && (
          <StoneDetailScreen
            stoneId={stoneId}
            onBack={() => navigate('collection')}
            onNavigate={navigate}
          />
        )}
      </Layout>
    </>
  );
}
